import { useState, ChangeEvent } from 'react';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

import ProgressIndicator from 'components/global/ProgressIndicator';
import PasswordRequirements from 'components/account/Password/PasswordRequirements';
import { validatePassword } from 'components/account/Password/Password.helpers';

import { validateForgotPassword_Gql } from 'gql/user/validateForgotPassword';
import { ValidateForgotPassword, ValidateForgotPasswordVariables } from 'models/GeneratedModels';
import { accountLinks, loginLink } from 'utils/links';
import StyledRouterLink from 'components/global/StyledRouterLink';

interface PasswordFields {
  password: string;
  confirmPassword: string;
}

interface ChangePasswordProps {
  code: string;
  redirectToLogin: boolean;
}

const UpdatePassword = ({ code, redirectToLogin }: ChangePasswordProps) => {
  const history = useHistory();

  const [errorText, setErrorText] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showExpiredLink, setShowExpiredLink] = useState(false);

  const [password, setPassword] = useState<PasswordFields>({
    password: '',
    confirmPassword: '',
  });

  const [savePassword, { loading }] = useMutation<ValidateForgotPassword, ValidateForgotPasswordVariables>(
    validateForgotPassword_Gql
  );

  const onFormElementChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = e.target.name;
    const value = e.target.value;

    setPassword({
      ...password,
      [name]: value,
    });
  };

  const savePasswordUpdate = async () => {
    setErrorText('');
    setShowExpiredLink(false);

    const passwordValidation = validatePassword(password.password, password.confirmPassword);
    if (passwordValidation.isValid) {
      try {
        const result = await savePassword({
          variables: {
            code,
            password: password.password,
          },
        });

        const response = result.data?.validateForgotPassword;

        if (response?.isSuccess) {
          setPassword({ password: '', confirmPassword: '' });
          setShowConfirmation(true);

          if (redirectToLogin) {
            history.push(loginLink.href);
          }
        } else if (response?.codeExpired) {
          setShowExpiredLink(true);
          setErrorText(response.message || 'Your password reset link has expired.');
        } else {
          setErrorText(response?.message || 'Failed to reset password. Please try again.');
        }
      } catch (error: any) {
        const errorMessage = error.graphQLErrors?.[0]?.message || error.message || 'An error occurred';
        setErrorText(errorMessage);
      }
    } else {
      setErrorText(passwordValidation.message);
    }
  };

  // Function to check if all required fields are filled
  const areAllFieldsFilled = () => {
    return password.password && password.confirmPassword;
  };

  return (
    <>
      <ProgressIndicator isOpen={loading} title="Saving..." />

      <Grid container>
        <Grid xs={3}></Grid>
        <Grid xs={6}>
          <Stack spacing={2}>
            {showConfirmation && <Alert severity="success">Password has been updated</Alert>}

            {showExpiredLink && (
              <Alert severity="info">
                Your password reset link has expired. Please{' '}
                <StyledRouterLink to={accountLinks.forgotPassword.href} text="click here" /> to request another
                password reset.
              </Alert>
            )}

            <TextField
              autoFocus
              margin="dense"
              name="password"
              label="Password"
              type="password"
              variant="outlined"
              color="warning"
              value={password.password}
              onChange={onFormElementChange}
              required
            />
            <PasswordRequirements />
            <TextField
              sx={{ mt: 2 }}
              margin="dense"
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              variant="outlined"
              color="warning"
              value={password.confirmPassword}
              onChange={onFormElementChange}
              required
            />

            {errorText.length > 0 && <Alert severity="error">{errorText}</Alert>}

            <Box sx={{ textAlign: 'right' }}>
              <Button 
                onClick={savePasswordUpdate} 
                variant="contained" 
                color="primary"
                disabled={!areAllFieldsFilled()}
              >
                Update
              </Button>
            </Box>
          </Stack>
        </Grid>
        <Grid xs={3}></Grid>
      </Grid>
    </>
  );
};

export default UpdatePassword;
